// 위험도 채널 **자동 주입의 인과**를 저작 화면에 드러내기 위한 판정 + 문구.
//
// 서버 게이트(`templateNeedsRiskConfigInjection`, apps/server/src/report-composer/report-composer.service.ts)는
// **둘 중 하나**면 평가기준(riskAssessmentMethodType)과 기준표 rows(risk-*-criteria-rows)를 작성폼에
// 주입한다(`injectRiskConfigChannels`):
//  1. 작성폼 위젯 트리에 `riskEstimation.` / `riskEstimationAfter.` 접두 bindingKey가 하나라도 있다
//     (`templateHasNestedRiskBinding` — branch+select 조립본).
//  2. 위험도 등록형 위젯(`widget:'registered'` + `widgetKey:'risk-estimation'`)을 배치했다 — dataSources
//     행 그리드 선언이 정본 채널이고, 작성폼 트리의 registered InputWidget도 본다
//     (`templatePlacesRegisteredWidget`).
//
// 즉 **경로(또는 위젯 키)를 고르는 행위 자체가 스위치**인데 그 인과가 서버 코드에만 있어서, 새로 양식을
// 만드는 저작자는 "왜 어떤 템플릿에서만 기준표가 나오는지"를 재현할 수 없었다. 판정을 여기 한 곳에 두고
// 저작 표면들이 같은 문구를 쓰게 한다 — 표면마다 문구를 따로 적으면 서버 규칙이 바뀔 때 일부만 낡는다.
// (2026-09-14 은닉 감사 B: 종전 문구가 1번 조건만 말해 2번으로 켜진 템플릿의 인과가 여전히 은닉이었다.)

/** 서버 게이트와 같은 접두 판정. 접두 목록이 서버와 어긋나면 안내가 거짓말이 된다. */
export const NESTED_RISK_KEY_PREFIXES = ['riskEstimation.', 'riskEstimationAfter.'] as const;

/** 서버 게이트 2번 조건의 위젯 키 — `templatePlacesRegisteredWidget(template, 'risk-estimation')`. */
export const RISK_INJECTION_WIDGET_KEY = 'risk-estimation';

/** bindingKey가 서버 주입 게이트를 켜는 중첩 위험도 경로인가(게이트 1번 조건). */
export function isNestedRiskBindingKey(bindingKey: string | undefined): boolean {
  if (!bindingKey) return false;
  return NESTED_RISK_KEY_PREFIXES.some((p) => bindingKey.startsWith(p));
}

/**
 * 위젯 키가 위험도 등록형 위젯인가(게이트 2번 조건) — dataSources 필드 표의 '위젯 키' select에서 같은
 * 안내를 낼지 판정. 이 키를 고른 필드는 **배치 자체로** 게이트가 켜진다(자식 경로를 따로 배치하지
 * 않아도). 계약 자식 경로(`<필드>.<자식>`)를 행에 배치하면 1번 조건으로도 켜진다.
 */
export function widgetKeyImpliesRiskInjection(widgetKey: string | undefined): boolean {
  return widgetKey === RISK_INJECTION_WIDGET_KEY;
}

/**
 * 작성폼 트리 위젯 하나가 서버 주입 게이트를 켜는가 — 두 조건의 합(1번 중첩 경로 ∪ 2번 등록형 위젯).
 * 트리 위젯 속성 패널이 안내를 낼지 판정한다. 종전엔 1번만 봐서, 트리에 registered/risk-estimation
 * 위젯을 둔 템플릿은 안내 없이 기준표가 나왔다(인과 은닉).
 */
export function treeWidgetTriggersRiskInjection(w: {
  bindingKey?: string;
  widget?: string;
  widgetKey?: string;
}): boolean {
  return (
    isNestedRiskBindingKey(w.bindingKey) ||
    (w.widget === 'registered' && widgetKeyImpliesRiskInjection(w.widgetKey))
  );
}

/** 저작 표면(행 필드 select · 위젯 키 select · 트리 위젯 패널)이 공유하는 안내 문구 — 두 조건을 다 말한다. */
export const RISK_NESTED_BINDING_HINT =
  '중첩 위험도 경로(riskEstimation.*)를 행에 배치하거나 위험도 등록형 위젯(widgetKey risk-estimation)을 ' +
  '배치하면 서버가 평가기준(riskAssessmentMethodType)과 기준표 rows(risk-*-criteria-rows)를 작성폼에 ' +
  '자동 공급합니다 — 별도 설정이 필요 없습니다.';
